import React, { useEffect, useMemo, useState } from 'react';
import * as styles from './HomeV3.module.scss';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import RedNumberText from '../../../components/RedNumberText/RedNumberText';
import LinkCardV2 from '../../../components/LinkCard/V2/LinkCardV2';
import SmallLinksBar from '../../../components/SmallLinksBar/SmallLinksBar';
import PhonePopup from '../../../components/PhonePopup/PhonePopup';
import { usePhonePopup } from '../../../components/PhonePopup/hooks/usePhonePopup';
import {
  formatIncrementedValue,
  formatLinksV3,
  getV3SmallLinks,
} from '../helpers';
import Location from '../../../components/Location/Location';

const HomeV2 = ({ pageContext }) => {
  const { data } = pageContext;

  const [subheader, setSubheader] = useState(() =>
    formatIncrementedValue(data.V2_Subheader)
  );

  const [links, setLinks] = useState(() => formatLinksV3(pageContext));
  const [opacities, setOpacities] = useState({ scroll: 1, header: 0 });

  const smallLinks = useMemo(() => getV3SmallLinks(pageContext), [pageContext]);
  const avatarImage = useMemo(
    () => getImage(data.Cover_Photo_Desktop?.localFiles?.[0]?.childImageSharp),
    [data.Cover_Photo_Desktop]
  );

  const { isPhonePopupVisible, phoneNumber, closePhonePopup } =
    usePhonePopup(data);

  const backgroundColor = useMemo(
    () =>
      !data.V2BackgroundImage
        ? (data.V2BackgroundColor ?? '#33aa331a')
        : 'transparent',
    [data.V2BackgroundColor, data.V2BackgroundImage]
  );

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      const maxScroll = 312;

      setOpacities({
        scroll: Math.max(0, 1 - scrollPosition / maxScroll),
        header: Math.min(1, scrollPosition / maxScroll),
      });
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const timers = [
      setTimeout(() => {
        setLinks(formatLinksV3(pageContext, -1));
        setSubheader(formatIncrementedValue(data.V2_Subheader, -1));
      }, 1 * 1000),
      setTimeout(() => {
        setLinks(formatLinksV3(pageContext, -2));
        setSubheader(formatIncrementedValue(data.V2_Subheader, -2));
      }, 3 * 1000),
    ];

    return () => timers.forEach(clearTimeout);
  }, [data.V2_Subheader, pageContext]);

  return (
    <div className={styles.container}>
      <div className={styles.backgroundImageContainer}>
        {avatarImage && (
          <GatsbyImage
            image={avatarImage}
            alt="Background Image"
            className={styles.backgroundImage}
          />
        )}
      </div>

      <div className={styles.mainCard} style={{ backgroundColor }}>
        <div
          className={styles.profileHeader}
          style={{ opacity: opacities.header }}
        >
          <div className={styles.profileHeaderAvatarAndName}>
            <div className={styles.profileHeaderAvatar}>
              {avatarImage && (
                <GatsbyImage
                  image={avatarImage}
                  alt="Cover Photo Mobile"
                  className={styles.profileHeaderAvatar}
                />
              )}
            </div>

            {data.Full_Name}
          </div>
        </div>

        <div className={styles.content}>
          <div className={styles.rootProfilePhotoContainer}>
            <div
              className={styles.profilePhotoContainer}
              style={{ opacity: opacities.scroll }}
            >
              {avatarImage && (
                <GatsbyImage
                  image={avatarImage}
                  alt="Cover Photo Mobile"
                  className={styles.profilePhoto}
                />
              )}
            </div>
          </div>

          <div className={styles.scrollView}>
            <div className={styles.profileDetails}>
              <div className={styles.profileDetailsContent}>
                <h1 className={styles.profileName}>{data.Full_Name}</h1>
                <span className={styles.profileSlug}>@{data.Name}</span>
              </div>
            </div>

            <div className={styles.scrollGroup}>
              <div className={styles.profileSubHeaderContainer}>
                <h2 className={styles.profileSubHeader}>
                  <RedNumberText {...subheader} />
                </h2>

                {pageContext.data.V2Location && <Location />}

                <SmallLinksBar
                  smallLinks={smallLinks}
                  creatorName={data.Name}
                  disableDeeplinks={data.Disable_Deeplinks}
                />
              </div>

              <div className={styles.linksList}>
                {links.map((linkData) => (
                  <LinkCardV2
                    key={linkData.link}
                    linkData={linkData}
                    disableDeeplinks={data.Disable_Deeplinks}
                    creatorName={data.Name}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {isPhonePopupVisible && (
        <PhonePopup phoneNumber={phoneNumber} onClose={closePhonePopup} />
      )}
    </div>
  );
};

export default HomeV2;
